import { useLocation } from "wouter";
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { PageBackground } from "@/components/ui/page-background";
import { AppHeader } from "@/components/app-header";
import { Card } from "@/components/ui/card";
import { Calendar, Music, ArrowRight, Building, Users, BarChart, Settings } from "lucide-react";
import EventManagement from "@/pages/event-management";
import { SettingsModal } from "@/components/settings-modal";

export default function Home() {
  const [, setLocation] = useLocation();
  const { user, isLoading } = useAuth();
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  
  // For god users, we need to check ALL organisations, not just their organisationUsers
  // Since god users have access to all orgs but aren't explicitly linked
  const [hasArtistManagement, setHasArtistManagement] = useState(false);
  const [allOrganisations, setAllOrganisations] = useState<any[]>([]);
  const [checkingOrgs, setCheckingOrgs] = useState(true);
  
  useEffect(() => {
    async function checkArtistManagement() {
      if (!user) {
        setCheckingOrgs(false);
        return;
      }
      
      // If user is god, fetch all organisations to check for artist management
      if (user.role === 'god') {
        try {
          const response = await fetch('/api/organisations');
          if (response.ok) {
            const orgs = await response.json();
            const hasAM = orgs.some((org: any) => org.artistManagement === true);
            setHasArtistManagement(hasAM);
            setAllOrganisations(orgs);
          }
        } catch (error) {
          // Silently handle error
        }
        setCheckingOrgs(false);
      } else {
        // For non-god users, check their organisationUsers
        const hasAM = user.organisationUsers?.some(ou => 
          ou.organisation?.artistManagement === true
        ) || false;
        setHasArtistManagement(hasAM);
        setCheckingOrgs(false);
      }
    }
    
    checkArtistManagement();
  }, [user]);

  // Wait for auth and org check to load
  if (isLoading || checkingOrgs) {
    return (
      <PageBackground>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Music className="h-8 w-8 animate-pulse mx-auto mb-4 text-slate-700" />
            <p className="text-slate-700">Loading...</p>
          </div>
        </div>
      </PageBackground>
    );
  }

  // If user has artist management, show selection page
  if (hasArtistManagement) {
    return (
      <PageBackground>
        <div className="min-h-screen flex items-center justify-center px-4 py-8">
          <div className="max-w-4xl w-full">
            {/* Header */}
            <div className="text-center mb-10 mt-10">
              <h1 className="text-4xl font-bold text-slate-900 mb-3 drop-shadow-sm">
                Welcome back, {user?.username}!
              </h1>
            </div>

            {/* Selection Cards */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Event Management Card */}
              <Card
                className="bg-white/20 backdrop-blur-xl rounded-2xl border border-white/30 hover:bg-white/30 transition-all duration-300 cursor-pointer group hover:scale-[1.02] relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
                }}
                onClick={() => setLocation('/event-management')}
              >
                {/* Specular highlight on hover */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
                  }}
                />
                
                <div className="p-8 relative z-10">
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="w-20 h-20 bg-blue-600/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-blue-600/30 group-hover:bg-blue-600/30 transition-colors">
                      <Calendar className="h-10 w-10 text-blue-600" />
                    </div>
                    
                    <div>
                      <h2 className="text-2xl font-semibold text-slate-900 mb-2">
                        Event Management
                      </h2>
                      <p className="text-slate-600 mb-4">
                        Manage jam nights, open mics, and live events. Track participants, handle staging, and run your venues.
                      </p>
                      
                      <div className="flex flex-col gap-2 text-sm text-slate-500">
                        <div className="flex items-center justify-center gap-2">
                          <span className="w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                          <span>Participant Management</span>
                        </div>
                        <div className="flex items-center justify-center gap-2">
                          <span className="w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                          <span>Stage Rotation</span>
                        </div>
                        <div className="flex items-center justify-center gap-2">
                          <span className="w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                          <span>Venue Operations</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 text-blue-600 font-medium group-hover:gap-3 transition-all">
                      <span>Open Event Manager</span>
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              </Card>

              {/* Booking Management Card */}
              <Card
                className="bg-white/20 backdrop-blur-xl rounded-2xl border border-white/30 hover:bg-white/30 transition-all duration-300 cursor-pointer group hover:scale-[1.02] relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
                }}
                onClick={() => setLocation('/booking-management')}
              >
                {/* Specular highlight on hover */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
                  }}
                />
                
                <div className="p-8 relative z-10">
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="w-20 h-20 bg-purple-600/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-purple-600/30 group-hover:bg-purple-600/30 transition-colors">
                      <Music className="h-10 w-10 text-purple-600" />
                    </div>
                    
                    <div>
                      <h2 className="text-2xl font-semibold text-slate-900 mb-2">
                        Booking Management
                      </h2>
                      <p className="text-slate-600 mb-4">
                        Manage your artists, acts, and bookings. Track performances, handle contracts, and manage payments.
                      </p>
                      
                      <div className="flex flex-col gap-2 text-sm text-slate-500">
                        <div className="flex items-center justify-center gap-2">
                          <span className="w-1.5 h-1.5 bg-purple-600 rounded-full"></span>
                          <span>Artist Roster</span>
                        </div>
                        <div className="flex items-center justify-center gap-2">
                          <span className="w-1.5 h-1.5 bg-purple-600 rounded-full"></span>
                          <span>Booking Calendar</span>
                        </div>
                        <div className="flex items-center justify-center gap-2">
                          <span className="w-1.5 h-1.5 bg-purple-600 rounded-full"></span>
                          <span>Financial Tracking</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 text-purple-600 font-medium group-hover:gap-3 transition-all">
                      <span>Open Booking Manager</span>
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Additional Options */}
            <div className="mt-8 space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 text-center mb-4">Additional Options</h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Organisations Button */}
                {(user?.role === 'god' || user?.organisationUsers?.some(ou => ou.role === 'owner')) && (
                  <Card
                    className="bg-white/20 backdrop-blur-xl rounded-xl border border-white/30 hover:bg-white/30 transition-all duration-300 cursor-pointer group hover:scale-[1.02] p-4 h-full"
                    style={{
                      boxShadow: '0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                    }}
                    onClick={() => setLocation('/organisations')}
                  >
                    <div className="flex flex-col items-center justify-center gap-2 h-full min-h-[80px]">
                      <Building className="h-6 w-6 text-orange-600" />
                      <span className="text-sm font-medium text-slate-900 text-center">Organisations</span>
                    </div>
                  </Card>
                )}
                
                {/* Participants */}
                <Card
                  className="bg-white/20 backdrop-blur-xl rounded-xl border border-white/30 hover:bg-white/30 transition-all duration-300 cursor-pointer group hover:scale-[1.02] p-4 h-full"
                  style={{
                    boxShadow: '0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                  }}
                  onClick={() => setLocation('/participants')}
                >
                  <div className="flex flex-col items-center justify-center gap-2 h-full min-h-[80px]">
                    <Users className="h-6 w-6 text-green-600" />
                    <span className="text-sm font-medium text-slate-900 text-center">Participants</span>
                  </div>
                </Card>
                
                {/* Event Analytics */}
                {(user?.role === 'god' ? allOrganisations : user?.organisationUsers?.map(ou => ou.organisation) || []).map(org => (
                  <Card
                    key={`analytics-${org.id}`}
                    className="bg-white/20 backdrop-blur-xl rounded-xl border border-white/30 hover:bg-white/30 transition-all duration-300 cursor-pointer group hover:scale-[1.02] p-4 h-full"
                    style={{
                      boxShadow: '0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                    }}
                    onClick={() => setLocation(`/analytics/${org.id}`)}
                  >
                    <div className="flex flex-col items-center justify-center gap-2 h-full min-h-[80px]">
                      <BarChart className="h-6 w-6 text-indigo-600" />
                      <span className="text-sm font-medium text-slate-900 text-center">Event Analytics</span>
                      {(user?.role === 'god' ? allOrganisations.length : user?.organisationUsers?.length || 0) > 1 && (
                        <span className="text-xs text-slate-500 text-center">{org.name}</span>
                      )}
                    </div>
                  </Card>
                ))}
                
                {/* Booking Analytics */}
                {(user?.role === 'god' 
                  ? allOrganisations.filter(org => org.artistManagement) 
                  : user?.organisationUsers?.filter(ou => ou.organisation?.artistManagement).map(ou => ou.organisation) || []
                ).map(org => (
                  <Card
                    key={`booking-analytics-${org.id}`}
                    className="bg-white/20 backdrop-blur-xl rounded-xl border border-white/30 hover:bg-white/30 transition-all duration-300 cursor-pointer group hover:scale-[1.02] p-4 h-full"
                    style={{
                      boxShadow: '0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                    }}
                    onClick={() => setLocation(`/booking-analytics/${org.id}`)}
                  >
                    <div className="flex flex-col items-center justify-center gap-2 h-full min-h-[80px]">
                      <BarChart className="h-6 w-6 text-purple-600" />
                      <span className="text-sm font-medium text-slate-900 text-center">Booking Analytics</span>
                      {(user?.role === 'god' 
                        ? allOrganisations.filter(org => org.artistManagement).length 
                        : user?.organisationUsers?.filter(ou => ou.organisation?.artistManagement).length || 0
                      ) > 1 && (
                        <span className="text-xs text-slate-500 text-center">{org.name}</span>
                      )}
                    </div>
                  </Card>
                ))}
                
                {/* Settings */}
                <Card
                  className="bg-white/20 backdrop-blur-xl rounded-xl border border-white/30 hover:bg-white/30 transition-all duration-300 cursor-pointer group hover:scale-[1.02] p-4 h-full"
                  style={{
                    boxShadow: '0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                  }}
                  onClick={() => setIsSettingsModalOpen(true)}
                >
                  <div className="flex flex-col items-center justify-center gap-2 h-full min-h-[80px]">
                    <Settings className="h-6 w-6 text-slate-600" />
                    <span className="text-sm font-medium text-slate-900 text-center">Settings</span>
                  </div>
                </Card>
              </div>
              
              <p className="text-sm text-slate-500 text-center mt-4">
                You can switch between management modes at any time from the main menu
              </p>
            </div>
          </div>
        </div>
        
        {/* Settings Modal */}
        <SettingsModal
          isOpen={isSettingsModalOpen}
          onOpenChange={setIsSettingsModalOpen}
        />
      </PageBackground>
    );
  }

  // If user doesn't have artist management, show event management page
  return <EventManagement />;
}
